import React, { createContext, ReactNode, useContext, useState } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import {
    ComplementaryLanguageState,
    ComplementaryServiceCategoryState,
    VisibleContentScope,
    ZitoneoContentType,
    ZitoneoPreviewMode,
    ZitoneoViewMode,
} from '../types/zitoneoServicesTypes';
import { zitoneoRouteDefinitions, zitoneoRoutes } from '../utils/zitoneoRoutes';
import { decodeBase64 } from '../../../utils/encodeBase64';

interface ZitoneoUIContextType {
    viewMode: ZitoneoViewMode;
    changeViewMode: (view: ZitoneoViewMode) => void;
    previewMode: ZitoneoPreviewMode;
    changePreviewMode: (mode: ZitoneoPreviewMode) => void;
    selectedCategory: ComplementaryServiceCategoryState | null;
    handleSelectCategory: (
        category: ComplementaryServiceCategoryState | null
    ) => void;
    selectedImageTypeKey: string | null;
    handleSelectImageType: (imageTypeKey: string | null) => void;
    selectedRoomId: string | null;
    handleSelectRoom: (roomId: string | null) => void;
    idVisibleServicesDetail: number;
    handleVisibleServicesDetail: (idService: number) => void;
    focusedHotelServiceId: string | null;
    handleFocusHotelService: (idHotelService: string | null) => void;
    visibleLanguage: ComplementaryLanguageState | null;
    handleVisibleLanguage: (lang: ComplementaryLanguageState) => void;
    isLoading: boolean;
    handleLoading: (loading: boolean) => void;
    handleResetUI: (
        mapped?: ComplementaryLanguageState[],
        appLanguage?: string
    ) => void;
    visibleContentScope: VisibleContentScope;
    handleVisibleContentScope: (
        contentScope: VisibleContentScope,
        roomId?: string | null
    ) => void;
    visibleContentType: ZitoneoContentType;
    handleVisibleContentType: (contentType: ZitoneoContentType) => void;
}

const ZitoneoUIContext = createContext<ZitoneoUIContextType | undefined>(
    undefined
);

export const ProviderZitoneoUI = ({ children }: { children: ReactNode }) => {
    const history = useHistory();
    const { pathname } = useLocation();
    const hotelMatch = matchPath<{ idEncoded: string }>(pathname, {
        path: zitoneoRouteDefinitions.HOTEL_PRESENTATION,
    });
    const hotelId = hotelMatch?.params.idEncoded
        ? decodeBase64(hotelMatch.params.idEncoded)
        : null;
    const previewMatch = matchPath(pathname, {
        path: zitoneoRouteDefinitions.HOTEL_PREVIEW,
    });
    const settingsMatch = matchPath(pathname, {
        path: zitoneoRouteDefinitions.HOTEL_SETTINGS,
    });
    const hotelEditorMatch = matchPath<{
        contentType: ZitoneoContentType;
    }>(pathname, {
        path: zitoneoRouteDefinitions.HOTEL_EDITOR_HOTEL,
    });
    const roomEditorMatch = matchPath<{
        roomIdEncoded: string;
        contentType: ZitoneoContentType;
    }>(pathname, {
        path: zitoneoRouteDefinitions.HOTEL_EDITOR_ROOM,
    });
    const roomId = roomEditorMatch?.params.roomIdEncoded
        ? decodeBase64(roomEditorMatch.params.roomIdEncoded)
        : null;
    const viewMode = previewMatch
        ? ZitoneoViewMode.PREVIEW
        : settingsMatch
        ? ZitoneoViewMode.SETTINGS
        : ZitoneoViewMode.EDITOR;
    const visibleContentScope = roomEditorMatch
        ? VisibleContentScope.ROOM
        : VisibleContentScope.HOTEL;
    const routeContentType =
        roomEditorMatch?.params.contentType ||
        hotelEditorMatch?.params.contentType;
    const visibleContentType =
        routeContentType === ZitoneoContentType.IMAGES
            ? ZitoneoContentType.IMAGES
            : ZitoneoContentType.SERVICES;
    const [isLoading, setIsLoading] = useState(false);
    const handleLoading = (loading: boolean) => {
        setIsLoading(loading);
    };
    const changeViewMode = (view: ZitoneoViewMode) => {
        if (!hotelId) return;
        if (view === ZitoneoViewMode.PREVIEW) {
            history.push(zitoneoRoutes.HOTEL_PREVIEW(hotelId));
        } else if (view === ZitoneoViewMode.SETTINGS) {
            history.push(zitoneoRoutes.HOTEL_SETTINGS(hotelId));
        } else {
            navigateToEditor(visibleContentScope, visibleContentType, roomId);
        }
    };

    const [previewMode, setPreviewMode] = useState<ZitoneoPreviewMode>(
        ZitoneoPreviewMode.DESKTOP
    );

    const changePreviewMode = (mode: ZitoneoPreviewMode) => {
        setPreviewMode(mode);
    };

    const [visibleLanguage, setVisibleLanguage] =
        useState<ComplementaryLanguageState | null>(null);

    const handleVisibleLanguage = (lang: ComplementaryLanguageState) => {
        setVisibleLanguage(lang);
    };

    const [selectedCategory, setSelectedCategory] =
        useState<ComplementaryServiceCategoryState | null>(null);

    const [selectedImageTypeKey, setSelectedImageTypeKey] = useState<
        string | null
    >(null);

    const handleSelectImageType = (imageTypeKey: string | null) => {
        setSelectedImageTypeKey(imageTypeKey);
    };

    const handleSelectCategory = (
        category: ComplementaryServiceCategoryState | null
    ) => {
        setSelectedCategory(category);
    };

    const handleSelectRoom = (selectedRoomId: string | null) => {
        if (
            selectedRoomId &&
            visibleContentScope === VisibleContentScope.ROOM
        ) {
            navigateToEditor(
                VisibleContentScope.ROOM,
                visibleContentType,
                selectedRoomId
            );
        }
    };

    const navigateToEditor = (
        scope: VisibleContentScope,
        contentType: ZitoneoContentType,
        selectedRoomId: string | null
    ) => {
        if (!hotelId) return;
        if (scope === VisibleContentScope.ROOM) {
            if (!selectedRoomId) return;
            history.push(
                zitoneoRoutes.HOTEL_EDITOR_ROOM(
                    hotelId,
                    selectedRoomId,
                    contentType
                )
            );
            return;
        }
        history.push(zitoneoRoutes.HOTEL_EDITOR_HOTEL(hotelId, contentType));
    };

    const handleVisibleContentScope = (
        contentScope: VisibleContentScope,
        selectedRoomId = roomId
    ) => {
        navigateToEditor(contentScope, visibleContentType, selectedRoomId);
    };

    const handleVisibleContentType = (contentType: ZitoneoContentType) => {
        navigateToEditor(visibleContentScope, contentType, roomId);
    };

    const [idVisibleServicesDetail, setIdVisibleServicesDetail] = useState(0);
    const [focusedHotelServiceId, setFocusedHotelServiceId] = useState<
        string | null
    >(null);

    const handleVisibleServicesDetail = (idService: number) => {
        setIdVisibleServicesDetail(prevId =>
            prevId === idService ? 0 : idService
        );
    };

    const handleFocusHotelService = (idHotelService: string | null) => {
        setFocusedHotelServiceId(idHotelService);
    };

    const handleResetUI = (
        mapped: ComplementaryLanguageState[] = [],
        appLanguage?: string
    ) => {
        setPreviewMode(ZitoneoPreviewMode.DESKTOP);
        setSelectedCategory(null);
        setSelectedImageTypeKey(null);
        setIdVisibleServicesDetail(0);
        setFocusedHotelServiceId(null);

        const english = mapped.find(
            (lang: ComplementaryLanguageState) => lang.Code === 'en'
        );
        const appLang = mapped.find(
            (lang: ComplementaryLanguageState) => lang.Code === appLanguage
        );
        if (english) {
            handleVisibleLanguage(english);
        } else if (appLang) {
            handleVisibleLanguage(appLang);
        } else if (mapped.length > 0) {
            handleVisibleLanguage(mapped[0]);
        } else {
            setVisibleLanguage(null);
        }
    };

    return (
        <ZitoneoUIContext.Provider
            value={{
                viewMode,
                changeViewMode,
                previewMode,
                changePreviewMode,
                selectedCategory,
                handleSelectCategory,
                selectedImageTypeKey,
                handleSelectImageType,
                selectedRoomId: roomId,
                handleSelectRoom,
                idVisibleServicesDetail,
                handleVisibleServicesDetail,
                focusedHotelServiceId,
                handleFocusHotelService,
                visibleLanguage,
                handleVisibleLanguage,
                isLoading,
                handleVisibleContentScope,
                visibleContentScope,
                handleVisibleContentType,
                visibleContentType,
                handleLoading,
                handleResetUI,
            }}
        >
            {children}
        </ZitoneoUIContext.Provider>
    );
};

export const useZitoneoUI = (): ZitoneoUIContextType => {
    const context = useContext(ZitoneoUIContext);
    if (!context) {
        throw new Error('useZitoneoUI must be used within a ProviderZitoneoUI');
    }
    return context;
};
